/*
 * diyAudio Store — variant chassis specifications controller
 *
 * Loaded once from theme.liquid. The controller deliberately lives outside
 * the product section because LinkedOption replaces that section with AJAX.
 */
(() => {
  'use strict';

  const controllerName = 'diyAudioVariantSpecifications';

  if (window[controllerName] && typeof window[controllerName].refresh === 'function') {
    window[controllerName].refresh();
    return;
  }

  const rootSelector = '[data-variant-specifications]';
  const dimensionKeys = [
    'internal_height',
    'internal_width',
    'internal_depth',
    'external_height',
    'external_width',
    'external_depth',
    'faceplate_height',
    'faceplate_width',
    'faceplate_depth'
  ];
  const constructionKeys = [
    'heatsinks',
    'top_cover',
    'bottom_cover',
    'front_panel',
    'rear_panel'
  ];

  const parsedData = new WeakMap();
  let scheduledRender = null;
  let followUpRender = null;

  const hasValue = (value) => (
    value !== null && value !== undefined && String(value).trim() !== ''
  );

  function readSpecifications(root) {
    const dataId = root.dataset.specificationsDataId || `${root.id}-data`;
    const dataElement = document.getElementById(dataId);

    if (!dataElement) return null;
    if (parsedData.has(dataElement)) return parsedData.get(dataElement);

    try {
      const specifications = JSON.parse(dataElement.textContent);
      parsedData.set(dataElement, specifications);
      return specifications;
    } catch (error) {
      console.error('Could not read variant chassis specifications.', error);
      return null;
    }
  }

  function variantIdFromEvent(event, specifications) {
    const detail = event && event.detail;
    const detailedVariant = detail && (detail.variant || detail);
    const detailedId = detailedVariant && (
      detailedVariant.id || detailedVariant.variantId || detailedVariant.variant_id
    );

    if (detailedId && specifications[String(detailedId)]) {
      return String(detailedId);
    }

    return null;
  }

  function selectedVariantId(root, specifications, event) {
    const eventVariantId = variantIdFromEvent(event, specifications);
    if (eventVariantId) return eventVariantId;

    const targetForm = event && event.target && event.target.closest
      ? event.target.closest('form[action*="/cart/add"]')
      : null;
    const targetInput = targetForm && targetForm.querySelector('[name="id"]');

    if (targetInput && specifications[String(targetInput.value)]) {
      return String(targetInput.value);
    }

    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    if (urlVariantId && specifications[String(urlVariantId)]) {
      return String(urlVariantId);
    }

    const section = root.closest('.shopify-section') || document;
    const variantInputs = section.querySelectorAll('form[action*="/cart/add"] [name="id"]');

    for (const input of variantInputs) {
      if (specifications[String(input.value)]) return String(input.value);
    }

    return root.dataset.initialVariantId;
  }

  function setBlockVisibility(root, visible) {
    root.hidden = !visible;

    // Local's custom Liquid product block wrapper. Hiding this removes the
    // accordion heading, border, and spacing when the selected variant is empty.
    const localProductBlock = root.closest('.card-widget');
    if (localProductBlock) localProductBlock.hidden = !visible;
  }

  function renderRoot(root, event) {
    const specifications = readSpecifications(root);

    if (!specifications) {
      setBlockVisibility(root, false);
      return;
    }

    const variantId = selectedVariantId(root, specifications, event);
    const values = specifications[String(variantId)] || {};
    const hasDimensions = dimensionKeys.some((key) => hasValue(values[key]));
    const hasConstruction = constructionKeys.some((key) => hasValue(values[key]));
    const hasSpecifications = hasDimensions || hasConstruction;

    setBlockVisibility(root, hasSpecifications);
    if (!hasSpecifications) return;

    const table = root.querySelector('[data-specifications-table]');
    if (table) table.hidden = false;

    root.querySelectorAll('[data-dimensions-group]').forEach((group) => {
      group.hidden = !hasDimensions;
    });

    const constructionGroup = root.querySelector('[data-construction-group]');
    if (constructionGroup) constructionGroup.hidden = !hasConstruction;

    dimensionKeys.forEach((key) => {
      const cell = root.querySelector(`[data-spec="${key}"]`);
      if (cell) cell.textContent = hasValue(values[key]) ? String(values[key]) : '—';
    });

    constructionKeys.forEach((key) => {
      const row = root.querySelector(`[data-specification-row="${key}"]`);
      const cell = root.querySelector(`[data-spec="${key}"]`);
      const populated = hasValue(values[key]);

      if (row) row.hidden = !populated;
      if (cell) cell.textContent = populated ? String(values[key]) : '';
    });

    const constructionRows = root.querySelectorAll('[data-construction-group] tr');
    constructionRows.forEach((row) => {
      row.classList.remove('variant-specifications__section-start');
    });

    if (hasDimensions && hasConstruction) {
      const firstVisibleConstructionRow = Array.from(constructionRows).find(
        (row) => !row.hidden
      );
      if (firstVisibleConstructionRow) {
        firstVisibleConstructionRow.classList.add('variant-specifications__section-start');
      }
    }
  }

  function renderAll(event) {
    document.querySelectorAll(rootSelector).forEach((root) => {
      renderRoot(root, event);
    });
  }

  function schedule(event) {
    window.clearTimeout(scheduledRender);
    window.clearTimeout(followUpRender);

    scheduledRender = window.setTimeout(() => renderAll(event), 0);

    // Local and some apps update the hidden variant ID after the originating
    // option event. The follow-up captures that completed state.
    followUpRender = window.setTimeout(() => renderAll(event), 120);
  }

  window[controllerName] = {
    refresh: () => schedule()
  };

  document.addEventListener('change', schedule);

  [
    'variant:change',
    'variant:changed',
    'variantChange',
    'product:variant-change',
    'product:variant-changed',
    'king-linked-options:product-seamless-switched',
    'shopify:section:load'
  ].forEach((eventName) => document.addEventListener(eventName, schedule));

  window.addEventListener('popstate', schedule);
  window.addEventListener('pageshow', schedule);

  // Generic fallback for any app or future LinkedOption release that replaces
  // the product section without emitting one of the events above.
  if ('MutationObserver' in window) {
    const productReplacementObserver = new MutationObserver((records) => {
      const specificationsWereAdded = records.some((record) => (
        Array.from(record.addedNodes).some((node) => (
          node.nodeType === 1 && (
            node.matches(rootSelector) || node.querySelector(rootSelector)
          )
        ))
      ));

      if (specificationsWereAdded) schedule();
    });

    productReplacementObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }
})();